import { z } from "zod/v3";

import {
  isApiClientError,
  isApiResponseValidationError,
  isApiTransportError,
  isValidationApiError,
} from "../api/errors";
import {
  fileUsageSchema,
  type FileRecord,
  type FileUsage,
} from "../entities/files";
import { pluralize } from "./fileLabels";

const uuid = z.string().uuid();

export interface FilesSearch {
  /** Выбранная папка; без значения показаны все файлы. */
  folder?: string | undefined;
}

/**
 * Папка в адресе, чтобы выбор переживал перезагрузку и ссылку можно было
 * переслать. Значение не из UUID отбрасывается: сервер ответил бы на него 422,
 * а страница так и осталась бы с пустым списком.
 *
 * Ключ возвращается всегда, пусть и пустым: роутер подмешивает к результату
 * значения родительских маршрутов, и без явного `undefined` сырое значение из
 * адреса пережило бы проверку.
 */
export function validateFilesSearch(
  value: Record<string, unknown>,
): FilesSearch {
  const folder = uuid.safeParse(value.folder);

  return { folder: folder.success ? folder.data : undefined };
}

/**
 * Тип сбоя при чтении данных страницы — продолжением фразы «Не удалось
 * загрузить …: <причина>».
 *
 * Общее «не удалось» не отличало нехватку прав от упавшей базы и от сервера,
 * который старше интерфейса, поэтому причину приходилось искать в DevTools.
 * Здесь называется то, что известно клиенту без них. У серверной ошибки
 * добавляется код запроса: по нему запись находится в логах.
 */
export function loadFailureReason(error: unknown): string {
  if (isApiTransportError(error)) return "нет связи с сервером";
  if (isApiResponseValidationError(error)) {
    return "ответ сервера не подошёл интерфейсу — вероятно, сервер и интерфейс разных версий";
  }
  if (!isApiClientError(error)) return "неизвестная ошибка";
  if (error.code === "scope.not_selected")
    return "выберите организацию в шапке";
  if (error.status === 401) return "сессия завершилась, войдите снова";
  if (error.status === 403) return "недостаточно прав";
  if (error.status === 404)
    return "не найдено — возможно, доступ закрыт или запись удалена";
  if (error.status === 429) return "слишком много запросов, подождите немного";
  if (error.status >= 500) {
    return `ошибка на сервере (${error.status}), код запроса ${error.requestId}`;
  }

  return `сервер ответил ${error.status}`;
}

const createNeedsScope =
  "Выберите организацию в шапке: папка создаётся в конкретной организации, а в режиме «Все организации» непонятно, в какой.";
const moveNeedsScope =
  "Выберите организацию в шапке: в режиме «Все организации» непонятно, в чьих папках раскладывать файл.";

/**
 * Что сказать человеку об отказе.
 *
 * Раньше на любой 409 и 422 показывалось «файл не прошёл проверку или помещён
 * в карантин», и это была неправда: чаще всего формат просто не входил в
 * короткий список разрешённых. Хранилище больше не отказывает по формату,
 * поэтому и причины называются своими именами.
 */
export function fileMessage(error: unknown): string {
  if (isApiClientError(error)) {
    // Не про права: их достаточно. Неизвестно другое — чьим будет файл.
    if (error.code === "scope.not_selected")
      return "Выберите организацию в шапке: файл должен кому-то принадлежать, а в режиме «Все организации» непонятно, чей он.";
    if (error.status === 403)
      return "Недостаточно прав для этой операции с файлом.";
    if (error.status === 413) return "Файл больше, чем принимает хранилище.";
    if (error.status === 422)
      return "Файл не принят: он исполняемый либо повреждён при передаче. Повторите загрузку.";
    if (error.status === 409)
      return "С файлом нельзя выполнить эту операцию: загрузка не завершена или срок ссылки истёк.";
    if (error.status === 503)
      return "Хранилище временно недоступно. Файл остаётся приватным; повторите попытку позже.";
  }
  return "Не удалось обработать файл. Проверьте подключение и повторите попытку.";
}

export function renameMessage(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403)
      return "Недостаточно прав для переименования файла.";
    if (error.status === 404) return "Файл недоступен. Обновите список файлов.";
    if (error.status === 422)
      return "Укажите название файла длиной до 255 символов без разделителей пути и управляющих символов.";
    if (error.status === 409)
      return "Файл пока нельзя переименовать. Обновите список и повторите попытку.";
  }
  return "Не удалось переименовать файл. Проверьте подключение и повторите попытку.";
}

const usageKinds: Record<FileUsage["kind"], string> = {
  document_version: "версия документа",
  mail_attachment: "вложение письма",
  comment_attachment: "вложение обсуждения",
  material: "материал",
};

/** Место использования одной строкой: «версия документа «Договор»». */
export function usageLabel(usage: FileUsage): string {
  return usage.title
    ? `${usageKinds[usage.kind]} «${usage.title}»`
    : usageKinds[usage.kind];
}

/** Места использования из ответа `file.in_use`; неразборчивый ответ — пустой список. */
function usedInFromError(error: unknown): FileUsage[] {
  if (!isApiClientError(error)) return [];
  const parsed = z.array(fileUsageSchema).safeParse(error.details.used_in);

  return parsed.success ? parsed.data : [];
}

/**
 * Удаление сорвалось, потому что файл занят.
 *
 * Места называются из ответа сервера, а не общей фразой «не удалось»: человеку
 * нужно знать, где файл держат. Но список сервер отдаёт не всегда — только тому,
 * кто вправе файл удалить, — поэтому без него текст остаётся честным и без мест.
 */
export function deleteMessage(error: unknown, file: FileRecord): string {
  if (isApiClientError(error) && error.code === "file.in_use") {
    const places = usedInFromError(error);
    if (places.length > 0) {
      return `Файл «${file.original_name ?? file.filename}» нельзя удалить: он используется — ${places.map(usageLabel).join("; ")}. Сначала освободите его там.`;
    }
    const links = file.reference_count;

    return links > 0
      ? `Файл «${file.original_name ?? file.filename}» нельзя удалить: он используется (${links} ${pluralize(links, ["ссылка", "ссылки", "ссылок"])}).`
      : `Файл «${file.original_name ?? file.filename}» нельзя удалить: он уже используется в ERP.`;
  }
  if (isApiClientError(error) && error.status === 403) {
    return "Удалить файл может только тот, кто его загрузил, или администратор системы.";
  }

  return fileMessage(error);
}

const forbiddenFolders =
  "Недостаточно прав: менять папки может только сотрудник организации-владельца с правом «Управление папками файлов».";
const missingFolder =
  "Папка недоступна: её удалили или доступ к ней закрыли. Обновите страницу.";

/** Отказ при создании, переименовании и переносе папки. */
export function folderMessage(
  error: unknown,
  action: "create" | "rename" | "move",
): string {
  if (isApiClientError(error)) {
    if (error.code === "scope.not_selected") return createNeedsScope;
    if (error.status === 403) return forbiddenFolders;
    if (error.status === 404) return missingFolder;
    if (isValidationApiError(error) && error.details.fields.parent_id) {
      return action === "move"
        ? "Папку нельзя перенести сюда: нельзя поместить её внутрь самой себя или своей вложенной папки, а также в папку другой организации."
        : "Папку нельзя создать здесь: родительская папка должна принадлежать выбранной организации.";
    }
    if (isValidationApiError(error) && error.details.fields.name) {
      return "Название не принято: оно должно быть от 1 до 255 знаков.";
    }
    if (error.status === 409 && action === "create") return createNeedsScope;
    if (error.status === 422) {
      return action === "move"
        ? "Папку нельзя перенести сюда: родительская папка должна принадлежать той же организации и не быть вложенной в эту."
        : "Папка не сохранена: проверьте название и расположение.";
    }
    if (error.status === 503)
      return "Хранилище временно недоступно. Повторите попытку позже.";
  }

  return "Не удалось сохранить папку. Проверьте подключение и повторите попытку.";
}

/** Отказ при смене списка КПО. */
export function folderAccessMessage(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403) return forbiddenFolders;
    if (error.status === 404) return missingFolder;
    if (error.status === 422)
      return "Доступ не сохранён: список содержит недоступное КПО или саму организацию-владельца, которой доступ выдавать не нужно.";
    if (error.status === 503)
      return "Хранилище временно недоступно. Доступ не изменён; повторите попытку позже.";
  }

  return "Не удалось сохранить доступ КПО. Проверьте подключение и повторите попытку.";
}

/** Отказ при перемещении файла. */
export function moveMessage(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.code === "scope.not_selected") return moveNeedsScope;
    if (error.status === 403) return forbiddenFolders;
    if (error.status === 404)
      return "Файл или папка недоступны: их удалили или доступ закрыли. Обновите страницу.";
    if (error.status === 422)
      return "Файл можно переложить только в папку его организации. Выбранная папка принадлежит другой.";
    if (error.status === 503)
      return "Хранилище временно недоступно. Повторите попытку позже.";
  }

  return "Не удалось переложить файл. Проверьте подключение и повторите попытку.";
}
